/**
 * Node visual para registrar atividade na timeline de um negócio CRM
 *
 * RELEASE: 1.0.0 - 2026-05-31 - Versão inicial
 *
 * Exibe tipo da atividade, origem do deal, título e responsável configurados
 * no node `crm_create_activity`. Segue o mesmo padrão visual dos demais
 * nodes CRM (CrmCreateDealNode, CrmChangePhaseNode, CrmAssignSellerNode).
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import {
  ClipboardList,
  StickyNote,
  Phone,
  Mail,
  Calendar,
  Bell,
  MessageCircle,
  MapPin,
  User,
  Link2,
} from 'lucide-react';
import { FlowNodeData, CrmCreateActivityConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/** Traduz tipo de atividade em rótulo PT-BR + ícone */
const getActivityMeta = (type?: string) => {
  switch (type) {
    case 'note':     return { label: 'Nota',      Icon: StickyNote };
    case 'call':     return { label: 'Ligação',   Icon: Phone };
    case 'email':    return { label: 'E-mail',    Icon: Mail };
    case 'meeting':  return { label: 'Reunião',   Icon: Calendar };
    case 'task':     return { label: 'Lembrete',  Icon: Bell };
    case 'whatsapp': return { label: 'WhatsApp',  Icon: MessageCircle };
    case 'visit':    return { label: 'Visita',    Icon: MapPin };
    default:         return { label: '—',         Icon: ClipboardList };
  }
};

/** Rótulo do modo de localização do deal */
const getDealSourceLabel = (
  mode?: string,
  varName?: string,
): string => {
  if (mode === 'from_variable') {
    return varName ? `Negócio em {{${varName}}}` : 'Negócio em variável';
  }
  return 'Negócio do gatilho';
};

/** Rótulo do responsável */
const getAssigneeLabel = (
  mode?: string,
  name?: string,
  varName?: string,
): string | null => {
  switch (mode) {
    case 'manual':       return name || 'Usuário fixo';
    case 'deal_seller':  return 'Vendedor do negócio';
    case 'from_variable':return varName ? `{{${varName}}}` : 'Variável';
    default:             return null;
  }
};

export function CrmCreateActivityNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config as CrmCreateActivityConfig | undefined;

  const isConfigured = !!(config?.activity_type && config?.title);
  const meta = getActivityMeta(config?.activity_type);
  const Icon = meta.Icon;

  const dealLabel = getDealSourceLabel(config?.deal_source, config?.deal_variable);
  const assigneeLabel = getAssigneeLabel(
    config?.assignee_mode,
    config?.assignee_name,
    config?.assignee_variable,
  );

  return (
    <>
      {/* Toolbar flutuante (editar/duplicar/excluir) */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={nodeData.onEdit as (() => void) | undefined}
        onDuplicate={(nodeData as any).onDuplicate as (() => void) | undefined}
        onDelete={nodeData.onDelete as (() => void) | undefined}
        onTestFromHere={(nodeData as any).onTestFromHere as (() => void) | undefined}
        isVisible={selected}
      />

      {/* Handle de entrada (topo) */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-violet-500 !border-2 !border-white"
      />

      {/* Corpo do node */}
      <div
        className={`
          min-w-[220px] max-w-[280px] bg-card rounded-lg border-2 shadow-lg
          ${selected ? 'border-violet-500 ring-2 ring-violet-500/30' : 'border-violet-400'}
          transition-all duration-200
        `}
      >
        {/* Cabeçalho */}
        <div className="flex items-center gap-2 p-3 border-b border-border bg-violet-500/10 rounded-t-lg">
          <div className="p-1.5 rounded bg-violet-500">
            <ClipboardList className="h-4 w-4 text-white" />
          </div>
          <span className="font-semibold text-sm truncate">
            {config?.nickname || 'Criar Atividade'}
          </span>
        </div>

        {/* Conteúdo */}
        <div className="p-3 space-y-2 text-xs">
          {isConfigured ? (
            <>
              {/* Tipo */}
              <div className="flex items-center gap-1.5">
                <Icon className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Tipo:</span>
                <span className="font-medium">{meta.label}</span>
              </div>

              {/* Negócio */}
              <div className="flex items-center gap-1.5">
                <Link2 className="h-3 w-3 text-muted-foreground" />
                <span className="font-medium truncate" title={dealLabel}>
                  {dealLabel}
                </span>
              </div>

              {/* Título */}
              <div className="pt-1 border-t border-border/50">
                <span className="text-muted-foreground">Título: </span>
                <span className="font-medium" title={config!.title}>
                  {config!.title.length > 60 ? `${config!.title.slice(0, 60)}…` : config!.title}
                </span>
              </div>

              {/* Responsável */}
              {assigneeLabel && (
                <div className="flex items-center gap-1.5">
                  <User className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Responsável:</span>
                  <span className="font-medium truncate" title={assigneeLabel}>
                    {assigneeLabel}
                  </span>
                </div>
              )}

              {/* Variável de saída */}
              {config?.output_variable && (
                <div className="flex items-center gap-1.5 text-violet-600 dark:text-violet-400 pt-1 border-t border-border/50">
                  <span>→ Salva ID em:</span>
                  <code className="bg-violet-100 dark:bg-violet-900/30 px-1 rounded text-[10px]">
                    {`{{${config.output_variable}}}`}
                  </code>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted-foreground italic text-center py-2">
              Clique para configurar a atividade
            </p>
          )}
        </div>
      </div>

      {/* Handle de saída (base) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-violet-500 !border-2 !border-white"
      />
    </>
  );
}
